import React, { FC } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import {
    UIFormProps,
} from '#types/System.types';
import { FormPropsUIDescription, formVersionNumberPrefix } from '#types/System.Parameters.types';
import FlexCol from '../atoms/FlexCol';
import FieldLabel from '../atoms/FieldLabel';
import { formStore } from '../../store';

const { Title } = Typography;

interface FormPropsUIProps {
    /** Configuration object containing form property */
    uiFormProperty: UIFormProps;
}

type FormPropId = keyof typeof FormPropsUIDescription;
const isValidFormPropId = (id: string): id is FormPropId => {
    return id in FormPropsUIDescription;
};

/**
 * A read-only component that displays form properties.
 *
 * Renders form-level properties in a standardized format with labels and tooltips.
 * Currently supports:
 * - formNumber: Shows the form's unique identifier
 * - formType: Shows the form type's description
 * - formVersionNumber: Shows the form's version number
 *
 * The wrapper carries `data-pdf-field-group`, which the PDF export stylesheet
 * turns into `break-inside: avoid`. A label over a one-line `Title` is exactly
 * the shape that rule exists for; anything that can outgrow a page must not
 * carry it (SYD-2298).
 *
 * @component
 * @param {FormPropsUIProps} props - Component props
 * @param {UIFormProps} props.uiFormProperty - Configuration object for the form property UI element
 * @returns {JSX.Element} A read-only form property display
 */
const FormPropsUI: FC<FormPropsUIProps> = React.memo(({
    uiFormProperty,
}) => {
    const snap = useSnapshot(formStore.state);
    const { id } = uiFormProperty;

    let displayValue = '';

    switch (id as string) {
        case 'formNumber': {
            const formNumber = snap.form.data?.formNumber;
            const parentFormNumber = snap.formVersion.data?.parentFormNumber;
            const parentDenormalizedFormNumber = snap.formVersion.data?.parentDenormalizedData?.formNumber;
            displayValue = formNumber || parentFormNumber || parentDenormalizedFormNumber || '';
            break;
        }
        case 'formType': {
            const shortLabel = snap.formType.data?.description?.shortLabel;
            const docId = snap.formType.data?.docId;
            displayValue = shortLabel || docId || '';
            break;
        }
        case 'formVersionNumber': {
            const v = snap.formVersion.data?.formVersionNumber;
            displayValue = v ? formVersionNumberPrefix + v.toString() : '-';
            break;
        }
        default:
            displayValue = '';
    }
    
    const valueConfig = {
        displayValue,
        label: isValidFormPropId(id) ? FormPropsUIDescription[id].shortLabel : id,
        tooltip: isValidFormPropId(id) ? FormPropsUIDescription[id].longDescription : `${id} value`
    };
    
    return (
        <FlexCol 
            gap={0} 
            noGrow
            style={{
                marginRight: 10,
            }}
            {...uiFormProperty.props}
            data-pdf-field-group="true"
        >
            <FieldLabel
                label={valueConfig.label}
                tooltip={valueConfig.tooltip}
            />
            <Title 
                level={2}
                style={{
                    marginTop: -5,
                    marginBottom: 0,
                }}
            >
                {valueConfig.displayValue}
            </Title>
        </FlexCol>
    );
});

FormPropsUI.displayName = 'FormPropsUI';

export default FormPropsUI; 